import React, { useMemo } from "react";
import { DateRange } from "react-day-picker";
import { BorrowerFilters } from "@/types/borrower";
import { useAppStatusTransitions, useWorkflowAppsQuery } from "@/hooks/queries";
import { groupAppsIntoBuckets } from "./workflowBuckets";
import WorkflowCarousel from "./WorkflowCarousel";

interface WorkflowBoardProps {
  filters: BorrowerFilters;
  dateRange?: DateRange;
}

// "New / Processing" (index 0) and "Needs Input" (index 1) lead the board, in
// that order, followed by the deal-progress buckets. The carousel opens on
// "Needs Input" so it's the first thing visible; "New / Processing" sits just
// out of view to its left, one click away via the prev arrow.
const INITIAL_START_INDEX = 1;

const WorkflowBoard: React.FC<WorkflowBoardProps> = ({ filters, dateRange }) => {
  const { data, isLoading } = useWorkflowAppsQuery(filters, dateRange);
  // Fetched once here and threaded down to every card's AppStatusPill,
  // instead of each card independently subscribing to the same query.
  const { choices } = useAppStatusTransitions();
  const hasSearch = Boolean(filters.search?.trim());

  const buckets = useMemo(() => {
    const grouped = groupAppsIntoBuckets(data ?? [], dateRange);
    // Search: hide empty sections so the board only shows matching columns.
    return hasSearch ? grouped.filter((b) => b.borrowers.length > 0) : grouped;
  }, [data, dateRange, hasSearch]);

  const initialStart = hasSearch ? 0 : INITIAL_START_INDEX;

  return (
    <WorkflowCarousel
      key={hasSearch ? "search" : "browse"}
      buckets={buckets}
      isLoading={isLoading}
      testId="workflow-carousel-active"
      visibleColumns={4}
      initialStartIndex={initialStart}
      statusChoices={choices}
    />
  );
};

export default WorkflowBoard;
